/**
 * HTTP failure from originator/provider/custodian APIs.
 * `code` is the backend domain code when present (e.g. ADM-001).
 */
export class ApiError extends Error {
  constructor(message, { status, code, payload } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status ?? null;
    this.code = code ?? null;
    this.payload = payload ?? null;
  }
}

export function isStaleAdmissionError(error) {
  if (!error) return false;
  if (error.code === 'STALE_ADMISSION_CONTEXT') return true;
  const message = String(error.message || '');
  return /admission assessment has changed/i.test(message);
}

export const STALE_ADMISSION_MESSAGE = 'Submission changed; review updated facts.';

/** Backend code raised when the Originator tries to change a package already in bank custody. */
export const PACKAGE_IN_CUSTODY_CODE = 'PACKAGE_IN_CUSTODY';
/** Backend code raised when a transfer / pledge / copy request targets an asset not yet admitted. */
export const ASSET_NOT_IN_CUSTODY_CODE = 'ASSET_NOT_IN_CUSTODY';
/** Backend code raised when a programme's custodian would change under admitted packages. */
export const PROGRAM_HAS_CUSTODY_CODE = 'PROGRAM_HAS_CUSTODY';
/** Backend code raised when the Originator edits Digitize values after certifying the package. */
export const PACKAGE_CERTIFIED_CODE = 'PACKAGE_CERTIFIED';

export const PACKAGE_CERTIFIED_MESSAGE =
  'This batch is certified and with the Provider / Custodian. Extracted values are locked until the Provider returns it.';
export const IN_CUSTODY_MESSAGE =
  'This package is in bank custody. The Originator can no longer change it; changes require a custody transaction.';
export const ASSET_NOT_IN_CUSTODY_MESSAGE =
  'This asset is not in bank custody yet. Requests can only be raised on admitted assets.';
export const PROGRAM_HAS_CUSTODY_MESSAGE =
  'This programme has packages in bank custody. The custodian cannot be changed without a custody transfer.';

export function isInCustodyError(error) {
  return Boolean(error) && error.code === PACKAGE_IN_CUSTODY_CODE;
}

/**
 * `fetch` rejects with a bare TypeError ("Failed to fetch", "NetworkError when
 * attempting to fetch resource", "Load failed") when the connection is cut
 * before any HTTP response arrives — typically an ingress / proxy timeout on a
 * long request or a dropped multi-GB upload, not a backend validation error.
 */
export function isNetworkFailure(error) {
  if (!error) return false;
  if (typeof error.status === 'number' && error.status > 0) return false;
  const message = String(error.message || '');
  return /failed to fetch|networkerror|load failed|network request failed/i.test(message);
}

export const NETWORK_FAILURE_MESSAGE =
  'The connection dropped before the server answered (browser reported "Failed to fetch"). ' +
  'For large batches this is usually a proxy or ingress timeout rather than a data problem — the server may still be working. Wait a moment and reload.';

/** Like formatApiFailure, but explains dropped connections instead of echoing "Failed to fetch". */
export function formatLoadFailure(error, fallback = 'Request failed.') {
  if (isNetworkFailure(error)) return NETWORK_FAILURE_MESSAGE;
  return formatApiFailure(error, fallback);
}

/**
 * Human-readable text for custody-freeze failures; falls back to the generic
 * formatter for everything else so callers can use this unconditionally.
 */
export function formatCustodyFailure(error, fallback = 'Request failed.') {
  if (!error) return fallback;
  if (error.code === PACKAGE_IN_CUSTODY_CODE) return IN_CUSTODY_MESSAGE;
  if (error.code === PACKAGE_CERTIFIED_CODE) return PACKAGE_CERTIFIED_MESSAGE;
  if (error.code === ASSET_NOT_IN_CUSTODY_CODE) return ASSET_NOT_IN_CUSTODY_MESSAGE;
  if (error.code === PROGRAM_HAS_CUSTODY_CODE) return PROGRAM_HAS_CUSTODY_MESSAGE;
  return formatApiFailure(error, fallback);
}

export function formatApiFailure(error, fallback = 'Request failed.') {
  const message =
    error && typeof error.message === 'string' && error.message.trim()
      ? error.message.trim()
      : fallback;
  const code = error && typeof error.code === 'string' ? error.code.trim() : '';
  if (code && !message.startsWith(`${code}:`) && message !== code) {
    return `${code}: ${message}`;
  }
  return message;
}
